import { memo, useEffect, useState, useLayoutEffect, useRef } from "react";
import { ArrowLeft, ArrowRight, CheckCircle2, Compass, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { JoyrideStep } from "../hooks/use-explorer-joyride";

type ExplorerJoyrideProps = {
  isRunning: boolean;
  stepIndex: number;
  totalSteps: number;
  currentStep: JoyrideStep;
  onNext: () => void;
  onPrev: () => void;
  onStop: () => void;
};

type TargetRect = {
  top: number;
  left: number;
  width: number;
  height: number;
  borderRadius: string;
  padding: number;
};

export const ExplorerJoyride = memo(
  ({ isRunning, stepIndex, totalSteps, currentStep, onNext, onPrev, onStop }: ExplorerJoyrideProps) => {
    const [targetRect, setTargetRect] = useState<TargetRect | null>(null);

    // Measure target element position whenever the step target changes or the
    // window resizes/scrolls. stepIndex alone never re-measures: steps sharing
    // a targetId measure the same element, so targetId is the real trigger.
    useLayoutEffect(() => {
      if (!isRunning) return () => {};

      const updateTargetRect = () => {
        let el = document.querySelector<HTMLElement>(`#${currentStep.targetId}`);
        if (!el && currentStep.targetId === "tour-export-nav") {
          el = document.querySelector<HTMLElement>("#mobile-nav-menu");
        }
        if (el) {
          const rect = el.getBoundingClientRect();
          // Add smooth scrolling to target if out of viewport
          if (rect.top < 0 || rect.bottom > (globalThis.innerHeight || document.documentElement.clientHeight)) {
            el.scrollIntoView({ behavior: "smooth", block: "nearest" });
          }

          // Measure live zoom factor applied by root container CSS zoom utilities (e.g. lg:zoom-90 / xl:zoom-95)
          let zoom = 1;
          if (el.offsetWidth && el.offsetWidth > 0) {
            const computedZoom = rect.width / el.offsetWidth;
            if (computedZoom > 0.5 && computedZoom < 1.8) {
              zoom = computedZoom;
            }
          }

          const computedStyle = globalThis.getComputedStyle(el);
          const borderRadius = computedStyle.borderRadius || "0.75rem";
          const isTabButton = currentStep.targetId === "tour-composite-tab";
          const pad = isTabButton ? 2 : 4;

          setTargetRect({
            top: rect.top / zoom,
            left: rect.left / zoom,
            width: rect.width / zoom,
            height: rect.height / zoom,
            borderRadius,
            padding: pad,
          });
        } else {
          setTargetRect(null);
        }
      };

      updateTargetRect();
      // Schedule secondary measurements: one after layout/state settles, and a
      // later one so mobile sheets / panel collapse/expand transitions finish.
      const settleTimer = setTimeout(updateTargetRect, 150);
      const lateTimer = setTimeout(updateTargetRect, 420);

      const handleResize = () => {
        updateTargetRect();
      };
      globalThis.addEventListener("resize", handleResize);
      globalThis.addEventListener("scroll", handleResize, { capture: true, passive: true });

      return () => {
        clearTimeout(settleTimer);
        clearTimeout(lateTimer);
        globalThis.removeEventListener("resize", handleResize);
        globalThis.removeEventListener("scroll", handleResize, { capture: true });
      };
    }, [isRunning, currentStep.targetId]);

    const onNextRef = useRef(onNext);
    const onPrevRef = useRef(onPrev);
    const onStopRef = useRef(onStop);

    useEffect(() => {
      onNextRef.current = onNext;
      onPrevRef.current = onPrev;
      onStopRef.current = onStop;
    });

    // Keyboard accessibility
    useEffect(() => {
      if (!isRunning) return () => {};

      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === "Escape") {
          onStopRef.current();
        } else if (e.key === "ArrowRight") {
          onNextRef.current();
        } else if (e.key === "ArrowLeft" && stepIndex > 0) {
          onPrevRef.current();
        }
      };

      globalThis.addEventListener("keydown", handleKeyDown);
      return () => {
        globalThis.removeEventListener("keydown", handleKeyDown);
      };
    }, [isRunning, stepIndex]);

    if (!isRunning) return null;

    const isLastStep = stepIndex === totalSteps - 1;

    // Empty badge text falls through to the step counter.
    const badgeText = currentStep.badgeText;
    let badgeDisplay = `Step ${stepIndex + 1} of ${totalSteps}`;
    if (badgeText) {
      badgeDisplay = badgeText;
    }

    // Calculate popover positioning relative to target element
    const getPopoverStyle = (): React.CSSProperties => {
      if (!targetRect) {
        return {
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
        };
      }

      const margin = 16;
      const windowWidth = globalThis.innerWidth || 1024;
      const popoverWidth = Math.min(380, windowWidth - 32);

      let top = targetRect.top + targetRect.height + margin;
      let left = targetRect.left + targetRect.width / 2 - popoverWidth / 2;

      if (currentStep.preferredPlacement === "top") {
        top = targetRect.top - 240 - margin;
      } else if (currentStep.preferredPlacement === "left") {
        left = Math.max(margin, targetRect.left - popoverWidth - margin);
        top = targetRect.top;
      } else if (currentStep.preferredPlacement === "right") {
        left = Math.min(windowWidth - popoverWidth - margin, targetRect.left + targetRect.width + margin);
        top = targetRect.top;
      }

      // Keep popover inside viewport bounds, reserving room for nav buttons
      left = Math.max(margin, Math.min(left, windowWidth - popoverWidth - margin));
      top = Math.max(80, Math.min(top, (globalThis.innerHeight || 768) - 340));

      return {
        position: "fixed",
        top: `${top}px`,
        left: `${left}px`,
        width: `${popoverWidth}px`,
      };
    };

    return (
      <div className='pointer-events-none fixed inset-0 z-[70] overflow-hidden font-sans' aria-live='polite'>
        {/* Target Element Spotlight Cutout & Dark Mask (No Blur, Crisp Focus, No Flashing) */}
        {targetRect ? (
          <div
            style={{
              position: "fixed",
              top: `${Math.max(0, targetRect.top - targetRect.padding)}px`,
              left: `${Math.max(0, targetRect.left - targetRect.padding)}px`,
              width: `${targetRect.width + targetRect.padding * 2}px`,
              height: `${targetRect.height + targetRect.padding * 2}px`,
              borderRadius: targetRect.borderRadius === "0px" ? "0.75rem" : targetRect.borderRadius,
            }}
            className='pointer-events-none border-2 border-primary bg-transparent shadow-[0_0_0_9999px_rgba(0,0,0,0.65)] transition-[top,left,width,height,border-radius] duration-300'
          />
        ) : (
          <div className='pointer-events-auto absolute inset-0 bg-black/65 transition-opacity duration-300' />
        )}

        {/* Tour Step Popover Card */}
        <div
          style={getPopoverStyle()}
          className='pointer-events-auto rounded-2xl border border-border bg-card/95 p-5 text-card-foreground shadow-2xl backdrop-blur-2xl transition-colors duration-300 animate-in fade-in-0 zoom-in-95'>
          {/* Header Badge & Close Button */}
          <div className='flex items-center justify-between gap-2 border-b border-border pb-3'>
            <div className='flex items-center gap-2'>
              <Compass className='size-4 text-primary animate-spin' />
              <Badge
                variant='secondary'
                className='rounded-md border border-primary/20 bg-primary/10 px-2.5 py-0.5 text-xs font-semibold text-primary uppercase tracking-wider'>
                {badgeDisplay}
              </Badge>
            </div>

            <button
              type='button'
              onClick={onStop}
              className='rounded-full p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-card-foreground cursor-pointer'
              aria-label='Close guided tour'>
              <X className='size-4' />
            </button>
          </div>

          {/* Title & Description */}
          <div className='mt-3 space-y-1.5'>
            <h3 className='text-base font-extrabold tracking-tight text-foreground'>{currentStep.title}</h3>
            <p className='text-xs leading-relaxed text-muted-foreground'>{currentStep.description}</p>
          </div>

          {/* Step Progress Dots */}
          <div className='mt-4 flex items-center justify-center gap-1.5'>
            {Array.from({ length: totalSteps }).map((_, idx) => (
              <div
                key={idx}
                className={`h-1.5 rounded-full transition-colors duration-300 ${
                  idx === stepIndex ? "w-6 bg-primary" : "w-1.5 bg-muted-foreground/30"
                }`}
              />
            ))}
          </div>

          {/* Navigation Buttons */}
          <div className='mt-4 flex items-center justify-between gap-2 border-t border-border pt-3'>
            <Button
              type='button'
              variant='ghost'
              size='sm'
              onClick={onStop}
              className='h-8 px-2.5 text-xs font-semibold text-muted-foreground hover:text-card-foreground'>
              Skip
            </Button>

            <div className='flex items-center gap-1.5'>
              {stepIndex > 0 && (
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  onClick={onPrev}
                  className='h-8 px-2.5 text-xs font-semibold'>
                  <ArrowLeft className='mr-1 size-3' /> Back
                </Button>
              )}

              <Button
                type='button'
                size='sm'
                onClick={onNext}
                className='h-8 px-3 text-xs font-bold bg-primary text-primary-foreground shadow-sm hover:bg-primary/90'>
                {isLastStep ? (
                  <>
                    <CheckCircle2 className='mr-1 size-3.5' /> Finish Tour
                  </>
                ) : (
                  <>
                    Next <ArrowRight className='ml-1 size-3.5' />
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  },
);

ExplorerJoyride.displayName = "ExplorerJoyride";
